import Vue from 'vue'
import api from '@/api'
import user from '@/api/modules/user'
import Cookie from '@/utils/cookie'
import { checkAdvisoryRole } from '@/utils/roles'

const getDefaultState = () => {
  return {
    authenticated: Boolean(Cookie.get('accessToken')),
    accessToken: Cookie.get('accessToken') || null,
    refreshToken: Cookie.get('refreshToken') || null,
    hasRole: Cookie.get('hasRole') || null,
    expiresIn: null,
    authExpire: null,
    user: null,
    userCreated: null
  }
}

const setTokens = (state, data) => {
  Vue.set(state, 'accessToken', data.accessToken)
  Vue.set(state, 'refreshToken', data.refreshToken)
  // Expiration time must be in seconds
  const expiration = data.expiresIn || 7200
  Vue.set(state, 'expiresIn', expiration)

  let d = new Date()
  d.setSeconds(d.getSeconds() + state.expiresIn)

  Cookie.set('accessToken', data.accessToken, d)
  Cookie.set('refreshToken', data.refreshToken, d)
  Cookie.set('authExpire', d, d)
  api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
  Vue.set(state, 'authenticated', true)
}

const state = getDefaultState()

const actions = {
  fetchUser (context, cached = true) {
    if (cached && state.user) {
      return Promise.resolve()
    } else {
      return user.fetch()
        .then((data) => {
          context.commit('saveUserData', data)
          context.commit('user/fetch', data, { root: true })
          context.commit('setRoleCookie')
          return Promise.resolve()
        })
        .catch(({ response }) => {
          if (response.status === 401) {
            context.commit('resetState')
            return Promise.reject(response.status)
          }
        })
    }
  },

  logIn (context, credentials) {
    return user.signIn(credentials)
      .then(data => {
        context.commit('saveTokens', data)
        return Promise.resolve()
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  mergeAccount (context, payload) {
    return user.mergeAccount(payload)
      .then(data => {
        return Promise.resolve(data)
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  updateConsent (context, payload) {
    return user.updateConsent(payload)
      .then(data => {
        return Promise.resolve(data)
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  validateAccount (context, payload) {
    return user.validateAccount(payload)
      .then(data => {
        context.commit('saveTokens', data.message)
        return Promise.resolve(data)
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  resendValidationMail (context, payload) {
    return user.resendValidationMail(payload)
      .then(data => {
        return Promise.resolve(data)
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  logOut (context) {
    api.defaults.headers.common['Authorization'] = null

    return user.signOut(state.accessToken)
      .then(() => {
        context.dispatch('reset', null, { root: true })
        return Promise.resolve()
      })
      .catch(({ response }) => {
        context.dispatch('reset', null, { root: true })
        return Promise.reject(response)
      })
  },

  refreshToken (context) {
    return user.refreshToken(state.refreshToken)
      .then(data => {
        context.commit('saveTokens', data)
        context.commit('setRoleCookie')
      })
  },

  confirmUser (context, code) {
    return user.confirm(code)
      .then((response) => {
        if (response.message === 'user.validated') {
          context.commit('confirmUser')
          context.commit('setRoleCookie')
        }
        return Promise.resolve()
      })
      .catch((response) => {
        return Promise.reject(response)
      })
  },

  resetState (context) {
    context.commit('resetState')
  },

  async signInKeycloakToken (context, { token }) {
    api.defaults.headers.common['Authorization'] = null

    context.commit('resetState')

    const data = await user.signInKeycloakToken(token)

    if (data && data.accessToken) {
      context.commit('saveTokensWithSetHeaderApi', data)
    } else {
      return Promise.reject(new Error('Authentication succeeded but no accessToken was provided.'))
    }
  }
}

const mutations = {
  authenticate (state) {
    Vue.set(state, 'authenticated', true)
  },

  saveTokens (state, data) {
    setTokens(state, data)
  },

  saveUserCreated (state, value) {
    Vue.set(state, 'userCreated', value)
  },

  saveUserData (state, data) {
    Vue.set(state, 'user', data)
  },

  setRoleCookie (state) {
    let d = new Date()
    d.setSeconds(d.getSeconds() + state.expiresIn)

    if (state.user.state === 'unconfirmed') {
      // This is to make sure we confirm users first!
      Cookie.set('hasRole', 'unconfirmed', d)
    } else {
      if (checkAdvisoryRole(state.user.roles)) {
        Cookie.set('hasRole', 'advisor', d)
      } else {
        Cookie.set('hasRole', 'customer', d)
      }
    }
  },

  confirmUser (state) {
    Vue.set(state.user, 'state', 'confirmed')
  },

  resetState (state) {
    Cookie.remove('accessToken')
    Cookie.remove('refreshToken')
    Cookie.remove('hasRole')
    Cookie.remove('authExpire')

    Object.assign(state, getDefaultState())
  },

  saveTokensWithSetHeaderApi(state, data) {
    setTokens(state, data)
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
  }
}

const getters = {
  isAdvisor (state) {
    return state.hasRole === 'advisor'
  },

  isCguValidated (state) {
    return state.user && state.user.cguValidated
  },

  isCustomer (state) {
    if (state.user) {
      return Boolean(state.user.roles.filter(role => role === 'customer').length)
    } else {
      return false
    }
  },

  isAdmin (state) {
    if (state.user) {
      return Boolean(state.user.roles.filter(role => role === 'admin').length)
    } else {
      return false
    }
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
